import { TireSeason } from '@navio/nsdk';
import { WheelSetTireMeasurementV2 } from 'noddi-types';
import { CarSideIcon, CarWheelPositions, cn } from 'noddi-ui-common';
import {
  NoddiBadge,
  NoddiBasicCard,
  NoddiButton,
  NoddiCardHeader,
  NoddiInfoField,
  type BadgeVariant
} from 'noddi-ui-web';
import { commonTexts, NoddiDateFormats, noddiFormatDate, type TreadDepthThresholds } from 'noddi-util';
import { useState } from 'react';
import { NavioTireSeasonChip } from '../../../CarWheel/NavioTireSeasonChip';
import { TreadDepthMeter, TreadDepthMeterLegend } from '../../../CarWheel/TreadDepthMeter';
import {
  getOverallWheelSetStatus,
  resolveWheelSetStatusLogo,
  treadDepthWheelSetStatus,
  wheelSetStatusToTranslation
} from '../../helpers';
import { WheelSetStatus, WheelSetStatusType } from '../../types';
import type { WheelSetCarWheelMeasurementValues } from './wheelSetCarWheelMeasurements';

export interface TireMeasurementTranslations {
  leftFront: string;
  rightFront: string;
  leftRear: string;
  rightRear: string;
  summerTires: string;
  winterTires: string;
  measuredAt: string;
  inAGoodCondition: string;
  missingMeasurements: string;
  soonWornOut: string;
  wornOut: string;
  treadDepthCondition: string;
  treadDepth: string;
  legalMinimum: string;
  recommendedMinimum: string;
  allSeasonTires: string;
  unknown: string;
  showMore: string;
  showLess: string;
}

interface TireMeasurementCardProps {
  tireSeason: TireSeason;
  /** The season's legal and recommended minimum in the car's country; null where neither is regulated. */
  thresholds: TreadDepthThresholds | null;
  translations?: TireMeasurementTranslations;
  measurements: WheelSetTireMeasurementV2 | WheelSetCarWheelMeasurementValues;
  collapsible?: boolean;
  /**
   * 'card' gives the block its own card chrome. Use 'plain' when it already sits inside a card —
   * a nested card there reads as a stray box rather than a section of the card it lives in.
   */
  surface?: 'card' | 'plain';
}

const statusBadgeVariant: Record<WheelSetStatusType, BadgeVariant> = {
  [WheelSetStatus.Satisfactory]: 'success',
  [WheelSetStatus.SoonWornOut]: 'warning',
  [WheelSetStatus.WornOut]: 'destructive',
  [WheelSetStatus.MissingMeasurements]: 'outline'
};

export const TireMeasurementCard = ({
  tireSeason,
  thresholds,
  translations,
  measurements,
  collapsible = false,
  surface = 'card'
}: TireMeasurementCardProps) => {
  const safeGuardTranslations = translations ?? commonTexts.wheelMeasurements;
  const [isExpanded, setIsExpanded] = useState(!collapsible);

  const tireMeasurements = [
    {
      positionText: safeGuardTranslations.leftFront,
      wheelPosition: CarWheelPositions.FRONT_LEFT,
      valueMm: measurements.frontLeft.value.value
    },
    {
      positionText: safeGuardTranslations.rightFront,
      wheelPosition: CarWheelPositions.FRONT_RIGHT,
      valueMm: measurements.frontRight.value.value
    },
    {
      positionText: safeGuardTranslations.leftRear,
      wheelPosition: CarWheelPositions.REAR_LEFT,
      valueMm: measurements.rearLeft.value.value
    },
    {
      positionText: safeGuardTranslations.rightRear,
      wheelPosition: CarWheelPositions.REAR_RIGHT,
      valueMm: measurements.rearRight.value.value
    }
  ];

  // Without thresholds there is nothing to grade against, so the card shows readings without a condition.
  const overallStatus = thresholds
    ? getOverallWheelSetStatus(tireMeasurements.map((tire) => treadDepthWheelSetStatus(tire.valueMm, thresholds)))
    : null;

  const conditionBadge = overallStatus && (
    <NoddiBadge variant={statusBadgeVariant[overallStatus]}>
      {resolveWheelSetStatusLogo(overallStatus)}
      {wheelSetStatusToTranslation(overallStatus, safeGuardTranslations)}
    </NoddiBadge>
  );

  const toggleButton = collapsible ? (
    <NoddiButton
      variant='ghost'
      size='small'
      startIcon={isExpanded ? 'ChevronUp' : 'ChevronDown'}
      className={cn('text-xs text-muted-foreground', isExpanded && '-ml-2 self-start')}
      aria-expanded={isExpanded}
      onClick={() => setIsExpanded((prev) => !prev)}
    >
      {isExpanded ? safeGuardTranslations.showLess : safeGuardTranslations.showMore}
    </NoddiButton>
  ) : null;

  if (collapsible && !isExpanded) {
    return (
      <div className='flex items-center justify-between gap-4'>
        {conditionBadge}
        {toggleButton}
      </div>
    );
  }

  const content = (
    <>
      <NoddiCardHeader
        title={safeGuardTranslations.treadDepth}
        isSmall
        headerNode={
          <NavioTireSeasonChip
            translations={{
              summerTires: safeGuardTranslations.summerTires,
              winterTires: safeGuardTranslations.winterTires,
              allSeasonTires: safeGuardTranslations.allSeasonTires
            }}
            season={tireSeason}
            size='sm'
          />
        }
      />

      <div className='flex flex-col gap-3'>
        {tireMeasurements.map((tire) => (
          <TreadDepthMeter
            key={tire.positionText}
            label={tire.positionText}
            valueMm={tire.valueMm}
            thresholds={thresholds}
            icon={<CarSideIcon carSide={tire.wheelPosition} width={16} height={26} />}
          />
        ))}
      </div>

      {thresholds && <TreadDepthMeterLegend thresholds={thresholds} translations={safeGuardTranslations} />}

      <div className='flex flex-col gap-2 text-sm'>
        {overallStatus === WheelSetStatus.Satisfactory && (
          <NoddiInfoField label={safeGuardTranslations.treadDepthCondition} value={conditionBadge} />
        )}

        <NoddiInfoField
          label={safeGuardTranslations.measuredAt}
          value={
            <NoddiBadge variant='outline'>
              {noddiFormatDate(measurements.frontLeft.measuredAt, NoddiDateFormats.DATE)}
            </NoddiBadge>
          }
        />
      </div>

      {collapsible && toggleButton}
    </>
  );

  // A hairline rule stands in for the card chrome, so the block reads as a section of the
  // surrounding card instead of a box inside a box.
  if (surface === 'plain') {
    return <div className='flex flex-col gap-4 border-t border-border pt-4'>{content}</div>;
  }

  return <NoddiBasicCard className='flex flex-col gap-4'>{content}</NoddiBasicCard>;
};
